/**
 * Primitivas de interface.
 *
 * Reproduzem a linguagem visual que a equipe já conhece — card branco
 * `rounded-2xl shadow-xl`, botão âmbar, faixa de feedback verde/vermelha,
 * spinner dentro do próprio botão — mas como componentes tipados em vez de
 * classes copiadas entre nove arquivos HTML.
 *
 * Acessibilidade que hoje não existe: `aria-invalid`, `aria-describedby`,
 * mensagem de erro associada ao campo e foco visível.
 */
import type { ButtonHTMLAttributes, InputHTMLAttributes, ReactNode, SelectHTMLAttributes, TextareaHTMLAttributes } from 'react';
import { forwardRef, useId } from 'react';
import { classesBadgeStatus, rotuloStatus } from '@tcm/shared';

export function cx(...classes: Array<string | false | null | undefined>): string {
  return classes.filter(Boolean).join(' ');
}

/* ------------------------------------------------------------------ */
/* Card                                                                */
/* ------------------------------------------------------------------ */

export function Card({
  children,
  className,
  titulo,
  acao,
}: {
  children: ReactNode;
  className?: string;
  titulo?: ReactNode;
  acao?: ReactNode;
}) {
  return (
    // `p-4` no celular: com `p-6` sobravam 279 px de conteúdo numa tela de 375,
    // e os campos de formulário ficavam estreitos demais para o dedo.
    <section className={cx('bg-white rounded-2xl shadow-xl p-4 sm:p-6 lg:p-8', className)}>
      {(titulo || acao) && (
        <header className="flex flex-wrap items-start justify-between gap-3 mb-5 sm:gap-4 sm:mb-6">
          {typeof titulo === 'string' ? (
            <h2 className="text-xl font-semibold text-slate-800">{titulo}</h2>
          ) : (
            titulo
          )}
          {acao}
        </header>
      )}
      {children}
    </section>
  );
}

/* ------------------------------------------------------------------ */
/* Botão                                                               */
/* ------------------------------------------------------------------ */

type VarianteBotao = 'primario' | 'secundario' | 'perigo' | 'sucesso' | 'fantasma';

/**
 * O `active:` existe para o toque: no celular não há `hover`, e sem uma
 * resposta imediata ao dedo o operador toca de novo achando que não pegou.
 */
const VARIANTES: Record<VarianteBotao, string> = {
  primario: 'bg-brand-600 text-white hover:bg-brand-700 active:bg-brand-700 focus-visible:outline-brand-600',
  secundario: 'bg-slate-200 text-slate-800 hover:bg-slate-300 active:bg-slate-300',
  perigo: 'bg-red-600 text-white hover:bg-red-700 active:bg-red-700',
  sucesso: 'bg-green-600 text-white hover:bg-green-700 active:bg-green-700',
  fantasma: 'bg-transparent text-slate-600 hover:bg-slate-100 active:bg-slate-100',
};

interface PropsBotao extends ButtonHTMLAttributes<HTMLButtonElement> {
  variante?: VarianteBotao;
  carregando?: boolean;
  /** Texto exibido enquanto carrega — 'Enviando…', 'Buscando…'. */
  textoCarregando?: string;
}

export function Botao({
  variante = 'primario',
  carregando = false,
  textoCarregando,
  disabled,
  className,
  children,
  ...resto
}: PropsBotao) {
  return (
    <button
      {...resto}
      // Desabilitar durante a requisição é o que impede o duplo clique gerar
      // dois lançamentos — hoje isso depende de cada tela lembrar de fazer.
      disabled={disabled || carregando}
      aria-busy={carregando}
      className={cx(
        'inline-flex items-center justify-center gap-2 rounded-lg px-5 py-2.5',
        // 44 px é o alvo de toque mínimo confortável; o `py-2.5` sozinho dava
        // 40 px. `min-h` e não `h`: botão com texto em duas linhas cresce.
        'min-h-11 touch-manipulation',
        'font-semibold transition-colors disabled:opacity-60 disabled:cursor-not-allowed',
        VARIANTES[variante],
        className,
      )}
    >
      {carregando && <Spinner />}
      {carregando && textoCarregando ? textoCarregando : children}
    </button>
  );
}

export function Spinner({ className }: { className?: string }) {
  return (
    <svg
      className={cx('animate-spin h-4 w-4', className)}
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.4 0 0 5.4 0 12h4z"
      />
    </svg>
  );
}

/* ------------------------------------------------------------------ */
/* Campos                                                              */
/* ------------------------------------------------------------------ */

export function Campo({
  rotulo,
  erro,
  dica,
  obrigatorio,
  children,
  id,
}: {
  rotulo: string;
  erro?: string | undefined;
  dica?: string;
  obrigatorio?: boolean;
  children: (props: { id: string; 'aria-invalid': boolean; 'aria-describedby': string | undefined }) => ReactNode;
  id?: string;
}) {
  const gerado = useId();
  const campoId = id ?? gerado;
  const descId = erro ? `${campoId}-erro` : dica ? `${campoId}-dica` : undefined;

  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={campoId} className="text-sm font-medium text-slate-700">
        {rotulo}
        {obrigatorio && <span className="text-red-600 ml-0.5">*</span>}
      </label>
      {children({ id: campoId, 'aria-invalid': Boolean(erro), 'aria-describedby': descId })}
      {erro ? (
        <p id={descId} className="text-sm text-red-600" role="alert">
          {erro}
        </p>
      ) : dica ? (
        <p id={descId} className="text-sm text-slate-500">
          {dica}
        </p>
      ) : null}
    </div>
  );
}

const CLASSES_CONTROLE =
  'w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-slate-800 ' +
  // Mesmo alvo de 44 px do botão: no celular o formulário é a tela onde o dedo
  // mais erra, e campo baixo demais faz o toque cair no rótulo vizinho.
  'min-h-11 ' +
  'placeholder:text-slate-400 transition-colors focus:border-brand-600 ' +
  'aria-[invalid=true]:border-red-500 disabled:bg-slate-100 disabled:text-slate-500';

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(
  function Input({ className, ...resto }, ref) {
    return <input ref={ref} className={cx(CLASSES_CONTROLE, className)} {...resto} />;
  },
);

export const AreaTexto = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement>>(
  function AreaTexto({ className, ...resto }, ref) {
    return <textarea ref={ref} className={cx(CLASSES_CONTROLE, 'min-h-24', className)} {...resto} />;
  },
);

export const Selecao = forwardRef<HTMLSelectElement, SelectHTMLAttributes<HTMLSelectElement>>(
  function Selecao({ className, children, ...resto }, ref) {
    return (
      <select ref={ref} className={cx(CLASSES_CONTROLE, className)} {...resto}>
        {children}
      </select>
    );
  },
);

/* ------------------------------------------------------------------ */
/* Status e feedback                                                   */
/* ------------------------------------------------------------------ */

/**
 * Badge de status. A cor sai da CATEGORIA do status, não de `includes` no
 * texto — é isso que corrige o branch morto em que 'Cancelado & Retirado'
 * casava antes com 'retirado' e aparecia verde.
 */
export function BadgeStatus({ codigo, rotulo }: { codigo: string | null; rotulo?: string | null }) {
  return (
    <span
      className={cx(
        'inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ring-inset',
        classesBadgeStatus(codigo),
      )}
    >
      {rotulo ?? rotuloStatus(codigo)}
    </span>
  );
}

/** Faixa de feedback acima do formulário, como nas telas atuais. */
export function Faixa({
  tipo,
  children,
  onFechar,
}: {
  tipo: 'sucesso' | 'erro' | 'aviso' | 'info';
  children: ReactNode;
  onFechar?: () => void;
}) {
  const estilos = {
    sucesso: 'bg-green-100 text-green-900 border-green-300',
    erro: 'bg-red-100 text-red-900 border-red-300',
    aviso: 'bg-yellow-100 text-yellow-900 border-yellow-300',
    info: 'bg-blue-100 text-blue-900 border-blue-300',
  } as const;

  return (
    <div
      role={tipo === 'erro' ? 'alert' : 'status'}
      className={cx('flex items-start gap-3 rounded-lg border px-4 py-3 text-sm', estilos[tipo])}
    >
      <div className="flex-1">{children}</div>
      {onFechar && (
        <button
          type="button"
          onClick={onFechar}
          aria-label="Fechar aviso"
          className="opacity-60 hover:opacity-100"
        >
          ✕
        </button>
      )}
    </div>
  );
}

/** Estado vazio com texto específico por contexto, como o sistema atual faz. */
export function Vazio({ titulo, descricao, acao }: { titulo: string; descricao?: string; acao?: ReactNode }) {
  return (
    <div className="flex flex-col items-center justify-center gap-2 py-12 text-center">
      <p className="text-lg font-semibold text-slate-700">{titulo}</p>
      {descricao && <p className="text-sm text-slate-500 max-w-md">{descricao}</p>}
      {acao}
    </div>
  );
}

/** Skeleton no lugar de "Carregando…". */
export function Esqueleto({ linhas = 3, className }: { linhas?: number; className?: string }) {
  return (
    <div className={cx('space-y-3', className)} aria-hidden="true">
      {Array.from({ length: linhas }, (_, i) => (
        <div key={i} className="h-4 rounded bg-slate-200 animate-pulse" style={{ width: `${100 - i * 8}%` }} />
      ))}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Tabela                                                              */
/* ------------------------------------------------------------------ */

export function Tabela({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div
      className={cx(
        // Sangra até a borda do card no celular (`p-4`), devolvendo 2 rem de
        // largura útil à rolagem — numa tela de 375 px isso é uma coluna
        // inteira a mais visível de cada vez.
        'overflow-x-auto scrollbar-fina -mx-4 px-4 sm:-mx-2 sm:px-2',
        // Sem isto o gesto de rolar a tabela até o fim vira "voltar página" no
        // Safari do iPhone, e o operador perde a tela.
        'overscroll-x-contain',
      )}
    >
      <table className={cx('w-full text-left text-sm', className)}>{children}</table>
    </div>
  );
}

/**
 * Coluna que fica presa à esquerda enquanto o resto da tabela rola.
 *
 * Só abaixo de `sm`, e por dois motivos: é lá que a tabela rola de lado (no
 * desktop ela cabe inteira), e é lá que não existe `hover` — presa, a célula
 * precisa de fundo próprio para o conteúdo não passar por baixo, e esse fundo
 * fixo brigaria com o realce de linha do mouse.
 *
 * Sem isto, rolar a tabela de clientes até a coluna "Situação" tira o NOME da
 * tela, e a linha vira uma fileira de dados sem dono.
 */
const COLUNA_FIXA = 'max-sm:sticky max-sm:left-0 max-sm:z-10 max-sm:bg-white';

export function Th({
  children,
  className,
  fixa = false,
}: {
  children?: ReactNode;
  className?: string;
  /** Prende a coluna à esquerda na rolagem horizontal do celular. */
  fixa?: boolean;
}) {
  return (
    <th
      scope="col"
      className={cx(
        'px-3 py-2 font-semibold text-slate-500 uppercase text-xs tracking-wide border-b border-slate-200',
        fixa && COLUNA_FIXA,
        className,
      )}
    >
      {children}
    </th>
  );
}

export function Td({
  children,
  className,
  fixa = false,
}: {
  children: ReactNode;
  className?: string;
  /** Prende a coluna à esquerda na rolagem horizontal do celular. */
  fixa?: boolean;
}) {
  return (
    <td className={cx('px-3 py-2.5 border-b border-slate-100', fixa && COLUNA_FIXA, className)}>
      {children}
    </td>
  );
}

/* ------------------------------------------------------------------ */
/* Paginação                                                           */
/* ------------------------------------------------------------------ */

export function Paginacao({
  page,
  totalPages,
  total,
  limit,
  onPage,
  onLimit,
}: {
  page: number;
  totalPages: number;
  total: number;
  limit: number;
  onPage: (p: number) => void;
  onLimit: (l: number) => void;
}) {
  return (
    <div className="flex flex-col gap-3 pt-4 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
      <div className="flex items-center gap-2 text-sm text-slate-600">
        {/* "Itens por página" quebrava em duas linhas no celular e empurrava o
            seletor para baixo; o rótulo curto diz a mesma coisa ao lado do
            campo, e o longo fica para o leitor de tela. */}
        <label htmlFor="itens-por-pagina">
          <span className="sm:hidden">Por página</span>
          <span className="hidden sm:inline">Itens por página</span>
        </label>
        <Selecao
          id="itens-por-pagina"
          className="w-auto py-1.5"
          value={limit}
          onChange={(e) => onLimit(Number(e.target.value))}
        >
          {[10, 20, 50].map((n) => (
            <option key={n} value={n}>
              {n}
            </option>
          ))}
        </Selecao>
        <span className="text-slate-400">·</span>
        <span className="whitespace-nowrap">{total} registro{total === 1 ? '' : 's'}</span>
      </div>

      {/* No celular os dois botões dividem a linha e a contagem fica no meio:
          alvos largos, longe um do outro, e o polegar alcança os dois. */}
      <div className="flex items-center justify-between gap-2 sm:justify-end">
        <Botao
          variante="secundario"
          className="flex-1 px-3 py-1.5 sm:flex-none"
          disabled={page <= 1}
          onClick={() => onPage(page - 1)}
        >
          Anterior
        </Botao>
        <span className="shrink-0 whitespace-nowrap text-sm text-slate-600 tabular-nums">
          <span className="hidden sm:inline">Página </span>
          {page}
          <span className="hidden sm:inline"> de </span>
          <span className="sm:hidden"> / </span>
          {Math.max(totalPages, 1)}
        </span>
        <Botao
          variante="secundario"
          className="flex-1 px-3 py-1.5 sm:flex-none"
          disabled={page >= totalPages}
          onClick={() => onPage(page + 1)}
        >
          Próxima
        </Botao>
      </div>
    </div>
  );
}
